import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StorageIcon from '@mui/icons-material/Storage';
import WorkIcon from '@mui/icons-material/Work';
import LinearProgress from '@mui/material/LinearProgress';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import axios from '../../../api/axiosInstance';
import PageHeader from '../../../components/common/PageHeader';

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatBytes(value) {
  if (value === null || value === undefined || value === '') return 'N/D';
  const bytes = Number(value);
  if (!Number.isFinite(bytes)) return '-';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return (bytes / (1024 ** index)).toFixed(index > 2 ? 2 : 1) + ' ' + units[index];
}

function getOrgLabel(org) {
  if (!org) return '-';
  return org.parent_name ? `${org.name} (${org.parent_name})` : org.name;
}

function firstValue(source, paths) {
  for (const path of paths) {
    const value = path.split('.').reduce((acc, key) => acc?.[key], source);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return '';
}

function formatPolicyValue(value) {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) return formatDate(value);
  return String(value);
}

function getBackupPolicyValue(row, paths) {
  return firstValue(row.rawData, paths) || firstValue(row.rawData?.backupPolicy, paths) || firstValue(row.rawData?.retentionPolicy, paths);
}

function formatTargetType(value) {
  const target = String(value || '');
  if (!target) return '-';
  if (/localfolder|local/i.test(target)) return 'Local';
  if (/cloudrepository|cloud/i.test(target)) return 'Cloud';
  return target;
}

function formatDuration(value) {
  const seconds = Number(value);
  if (!Number.isFinite(seconds)) return '-';
  if (seconds < 60) return seconds.toFixed(0) + ' s';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  if (minutes < 60) return minutes + ' min ' + remainingSeconds + ' s';
  return Math.floor(minutes / 60) + ' h ' + (minutes % 60) + ' min';
}

function getJobLastRun(row) {
  return row.lastRunAt || firstValue(row.rawData, ['lastRun', 'lastRunTime', 'lastSession.startTime', 'lastSession.creationTime']);
}

function isJobStale(row) {
  const value = getJobLastRun(row);
  if (!value) return true;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) || Date.now() - date.getTime() > 2 * 24 * 60 * 60 * 1000;
}

function getJobStatusColor(value) {
  const status = String(value || '').toLowerCase();
  if (/success|completed|ok/.test(status)) return 'success';
  if (/failed|fail|error/.test(status)) return 'error';
  if (/warning|warn/.test(status)) return 'warning';
  if (/running|active|working/.test(status)) return 'info';
  return 'default';
}

function isJobResource(resource) {
  const type = String(resource?.resourceType || resource?.resourcePath || '').toLowerCase();
  return type.includes('job');
}

export default function VcspPage({ view = 'repos' }) {
  const [resources, setResources] = useState([]);
  const [byType, setByType] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [selected, setSelected] = useState(null);

  const loadResources = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/vcsp');
      setResources(data.resources || []);
      setByType(data.byType || {});
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Impossible de charger VCSP.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadResources();
  }, [loadResources]);

  const syncResources = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post('/api/vcsp/sync');
      await loadResources();
      const errors = data.summary?.errors?.length || 0;
      setFeedback({
        type: errors ? 'warning' : 'success',
        message: `Synchronisation terminée : ${data.summary?.resources || 0} ressource(s), ${errors} erreur(s).`,
      });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.error || error.response?.data?.message || 'Synchronisation VCSP impossible.' });
    } finally {
      setSyncing(false);
    }
  };

  const repoColumns = useMemo(() => [
    { accessorKey: 'name', header: 'Dépôt / ressource', size: 260 },
    { accessorKey: 'Organization.name', header: 'Client', size: 220, Cell: ({ row }) => getOrgLabel(row.original.Organization) },
    { accessorKey: 'companyName', header: 'Société VCSP', size: 180 },
    { id: 'used', header: 'Utilisé', size: 145, accessorFn: (row) => row.usedBytes, Cell: ({ cell }) => formatBytes(cell.getValue()) },
    { id: 'total', header: 'Total', size: 145, accessorFn: (row) => row.capacityBytes, Cell: ({ cell }) => formatBytes(cell.getValue()) },
    { id: 'free', header: 'Disponible', size: 145, accessorFn: (row) => row.freeBytes, Cell: ({ cell }) => formatBytes(cell.getValue()) },
    { id: 'usage', header: 'Utilisation', size: 180, accessorFn: (row) => row.usagePercent, Cell: ({ cell }) => {
      const value = Number(cell.getValue());
      return Number.isFinite(value) ? <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 150 }}><LinearProgress variant="determinate" value={Math.min(100, Math.max(0, value))} sx={{ flex: 1 }} /><Typography variant="body2">{value.toFixed(1)}%</Typography></Stack> : '-';
    } },
    { accessorKey: 'lastSeenAt', header: 'Dernière synchro', size: 170, Cell: ({ cell }) => formatDate(cell.getValue()) },
    { id: 'actions', header: 'Actions', size: 80, enableColumnFilter: false, enableSorting: false, Cell: ({ row }) => <Tooltip title="Voir le JSON"><IconButton size="small" onClick={() => setSelected(row.original)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip> },
  ], []);

  const jobColumns = useMemo(() => [
    { accessorKey: 'status', header: 'Statut', size: 125, filterVariant: 'autocomplete', Cell: ({ cell }) => cell.getValue() ? <Chip size="small" label={cell.getValue()} color={getJobStatusColor(cell.getValue())} /> : '-' },
    { accessorKey: 'name', header: 'Job', size: 280 },
    { id: 'targetType', header: 'Cible', size: 130, filterVariant: 'autocomplete', accessorFn: (row) => firstValue(row.rawData, ['targetType', 'target.type', 'repositoryType']), Cell: ({ cell }) => <Chip size="small" label={formatTargetType(cell.getValue())} color={/cloud/i.test(String(cell.getValue() || '')) ? 'info' : /local/i.test(String(cell.getValue() || '')) ? 'default' : 'default'} /> },
    { id: 'freeSpace', header: 'Espace libre', size: 140, accessorFn: (row) => firstValue(row.rawData, ['freeSpace', 'freeSpaceBytes']), Cell: ({ cell }) => formatBytes(cell.getValue()) },
    { accessorKey: 'Organization.name', header: 'Client', size: 220, Cell: ({ row }) => getOrgLabel(row.original.Organization) },
    { accessorKey: 'companyName', header: 'Société VCSP', size: 180 },
    { accessorKey: 'lastRunStatus', header: 'État dernier lancement', size: 190, Cell: ({ cell }) => cell.getValue() && cell.getValue() !== 'status' ? <Chip size="small" label={cell.getValue()} color={getJobStatusColor(cell.getValue())} /> : '-' },
    { id: 'restorePoints', header: 'Points de restauration', size: 165, accessorFn: (row) => firstValue(row.rawData, ['restorePoints', 'restorePointCount']), Cell: ({ cell }) => cell.getValue() || '-' },
    { id: 'lastRunAt', header: 'Dernier lancement', size: 180, accessorFn: (row) => getJobLastRun(row), Cell: ({ cell, row }) => <Box sx={isJobStale(row.original) ? { color: 'error.main', fontWeight: 700 } : undefined}>{formatDate(cell.getValue())}</Box> },
    { id: 'lastEndTime', header: 'Fin du dernier lancement', size: 180, accessorFn: (row) => firstValue(row.rawData, ['lastEndTime', 'lastSession.endTime', 'lastSession.finishTime']), Cell: ({ cell }) => formatDate(cell.getValue()) },
    { id: 'lastDuration', header: 'Durée', size: 120, accessorFn: (row) => firstValue(row.rawData, ['lastDuration', 'lastDurationSeconds', 'duration']), Cell: ({ cell }) => formatDuration(cell.getValue()) },
    { accessorKey: 'lastSeenAt', header: 'Dernière synchro', size: 170, Cell: ({ cell }) => formatDate(cell.getValue()) },
    { id: 'actions', header: 'Actions', size: 80, enableColumnFilter: false, enableSorting: false, Cell: ({ row }) => <Tooltip title="Voir le JSON"><IconButton size="small" onClick={() => setSelected(row.original)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip> },
  ], []);

  const displayedResources = useMemo(() => {
    const isRepo = (resource) => (resource.resourceKind === 'repo' || (!resource.resourceKind && !isJobResource(resource))) && !String(resource.resourceType || '').endsWith('.usage');
    const filtered = resources.filter(view === 'repos' ? isRepo : (resource) => {
      if (!(resource.resourceKind === 'job' || isJobResource(resource))) return false;
      const isUuidName = /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(String(resource.name || ''));
      const raw = resource.rawData || {};
      const hasJobData = resource.status || resource.lastRunAt || raw.lastRun || raw.lastRunTime || raw.jobName || raw.name;
      return !isUuidName || Boolean(hasJobData);
    });
    if (view === 'repos') return filtered;
    const unique = new Map();
    filtered.forEach((resource) => {
      const target = firstValue(resource.rawData, ['targetType', 'target.type', 'repositoryType']);
      const raw = resource.rawData || {};
      const normalizedName = String(resource.name || '').trim().toLowerCase();
      const normalizedTarget = String(target || '').trim().toLowerCase();
      const instanceId = raw.instanceUid || raw.jobUid || raw.uniqueUid || resource.vcspUid;
      const key = normalizedName && !/^[0-9a-f]{8}-[0-9a-f-]{27,}$/.test(normalizedName)
        ? 'job:' + normalizedName + '|' + normalizedTarget
        : 'instance:' + String(instanceId).trim().toLowerCase();
      const current = unique.get(key);
      const completeness = (item) => [
        item.status,
        item.lastRunAt || firstValue(item.rawData, ['lastRun', 'lastRunTime']),
        firstValue(item.rawData, ['lastEndTime', 'lastSession.endTime']),
        item.rawData?.restorePoints,
        item.rawData?.freeSpace,
      ].filter((value) => value !== undefined && value !== null && value !== '').length;
      if (!current || completeness(resource) > completeness(current) || (completeness(resource) === completeness(current) && new Date(resource.lastSeenAt || 0) > new Date(current.lastSeenAt || 0))) {
        unique.set(key, resource);
      }
    });
    return Array.from(unique.values());
  }, [resources, view]);

  const table = useMaterialReactTable({
    columns: view === 'repos' ? repoColumns : jobColumns,
    data: displayedResources,
    localization: MRT_Localization_FR,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    initialState: {
      density: 'compact',
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 25, pageIndex: 0 },
    },
    renderDetailPanel: ({ row }) => {
      const resource = row.original;
      const raw = resource.rawData || {};
      const policy = raw.backupPolicy || raw.retentionPolicy || raw.policy || {};
      const schedule = raw.schedule || raw.scheduleOptions || raw.scheduling || {};
      const chaining = schedule.chainingOptions || raw.chainingOptions || {};
      const previousJob = resources.find((candidate) => candidate.vcspUid === chaining.previousJobId || candidate.rawData?.jobUid === chaining.previousJobId || candidate.rawData?.uid === chaining.previousJobId);
      const previousJobName = chaining.previousJobName || previousJob?.name || chaining.previousJobId;
      const policyRows = [
        ['Début planifié', schedule.startDateTime || schedule.startDateTimeUtc || schedule.startDate || 'Non fourni par VCSP'],
        ['Début du dernier backup', raw.lastRun],
        ['Planification', schedule.dailyScheduleOptions ? 'Quotidienne' : schedule.monthlyScheduleOptions ? 'Mensuelle' : schedule.periodicallyScheduleOptions ? 'Périodique' : schedule.continuousScheduleEnabled ? 'Continue' : (typeof schedule === 'string' ? schedule : '-')],
        ['Prochaine exécution', getBackupPolicyValue(resource, ['nextRun', 'nextRunTime', 'nextExecutionTime', 'schedule.nextRun'])],
        ['Rétention', getBackupPolicyValue(resource, ['retention', 'retentionDays', 'keepDays', 'retentionPolicy.retentionDays'])],
        ['Points conservés', getBackupPolicyValue(resource, ['restorePoints', 'restorePointCount', 'keepLast'])],
        ['GFS', getBackupPolicyValue(resource, ['gfs', 'gfsPolicy', 'grandfatherFatherSon'])],
        ['Fenêtre de sauvegarde', getBackupPolicyValue(resource, ['backupWindow', 'scheduleWindow', 'window'])],
        ['Sauvegarde continue', schedule.continuousScheduleEnabled],
      ];
      return (
        <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Politique du backup</Typography>
          {chaining.previousJobId && (
            <Box sx={{ mb: 1.5, p: 1, borderLeft: 3, borderColor: 'warning.main', bgcolor: 'background.paper' }}>
              <Typography variant="caption" color="text.secondary">Job chaîné</Typography>
              <Typography variant="body2">Origine : {resource.name}</Typography>
              <Typography variant="body2">Job précédent : {formatPolicyValue(previousJobName)}</Typography>
              <Typography variant="caption" color="text.secondary">UID précédent : {chaining.previousJobId}</Typography>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary">Planification détaillée</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {policyRows.map(([label, value]) => <Box key={label} sx={{ minWidth: 170 }}><Typography variant="caption" color="text.secondary">{label}</Typography><Typography variant="body2">{formatPolicyValue(value)}</Typography></Box>)}
          </Stack>
          {Object.keys(schedule).length > 0 && <Box component="pre" sx={{ mt: 1, mb: 0, fontSize: 11, whiteSpace: 'pre-wrap' }}>{JSON.stringify(schedule, null, 2)}</Box>}
          {Object.keys(policy).length > 0 && <Box component="pre" sx={{ mt: 1, mb: 0, fontSize: 11, whiteSpace: 'pre-wrap' }}>{JSON.stringify(policy, null, 2)}</Box>}
        </Box>
      );
    },
    state: { isLoading: loading || syncing },
  });

  const topTypes = useMemo(() => Object.entries(byType).sort((a, b) => b[1] - a[1]).slice(0, 6), [byType]);
  const jobStatusCounts = useMemo(() => {
    const jobs = resources.filter(isJobResource);
    return jobs.reduce((acc, job) => {
      const key = String(job.status || 'Inconnu');
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [resources]);

  const jobCount = useMemo(() => resources.filter(isJobResource).length, [resources]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
          <PageHeader section="Infrastructure" title={view === 'repos' ? 'VCSP - Repos' : 'VCSP - Jobs'} subtitle={view === 'repos' ? 'Dépôts et consommations synchronisés depuis Veeam Service Provider Console.' : 'Jobs et derniers lancements synchronisés depuis Veeam Service Provider Console.'} />
          <Stack direction="row" spacing={1}>
            <Button startIcon={<RefreshIcon />} variant="outlined" onClick={loadResources} disabled={loading || syncing}>Actualiser</Button>
            <Button startIcon={<SyncIcon />} variant="contained" onClick={syncResources} disabled={loading || syncing}>
              {syncing ? 'Synchronisation...' : 'Synchroniser'}
            </Button>
          </Stack>
        </Stack>

        {feedback && (
          <Alert severity={feedback.type} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 220 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <StorageIcon color="success" />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>{resources.length}</Typography>
                <Typography variant="body2" color="text.secondary">Ressources</Typography>
              </Box>
            </Stack>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 180 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <WorkIcon color="primary" />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>{jobCount}</Typography>
                <Typography variant="body2" color="text.secondary">Jobs</Typography>
              </Box>
            </Stack>
          </Paper>
          {topTypes.map(([type, count]) => (
            <Paper key={type} variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 180 }}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>{count}</Typography>
              <Typography variant="body2" color="text.secondary">{type}</Typography>
            </Paper>
          ))}
        </Stack>

        {view === 'jobs' && displayedResources.some(isJobStale) && (
          <Alert severity="error">
            {displayedResources.filter(isJobStale).length} job(s) n'ont pas été lancés depuis plus de 2 jours. Vérifiez leur état et leur planification.
          </Alert>
        )}

        {view === 'jobs' && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {Object.entries(jobStatusCounts).sort((a, b) => b[1] - a[1]).map(([status, count]) => (
              <Chip key={status} label={status + ' : ' + count} color={getJobStatusColor(status)} variant="outlined" />
            ))}
          </Stack>
        )}

        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          {loading ? <CircularProgress /> : <MaterialReactTable table={table} />}
        </Paper>
      </Stack>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>{selected?.name || 'Détail VCSP'}</DialogTitle>
        <DialogContent dividers>
          <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
            {selected ? JSON.stringify(selected.rawData || selected, null, 2) : ''}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
